import { CALLOUT_MODES, type CalloutMode } from '@tv-casino/shared';
import { AMBIENCE_GAIN, MASTER_GAIN, type SoundMix } from './soundDirector.js';
import type { AmbienceLevel } from './soundCues.js';

/**
 * One device's own audio — the phone's answer to `soundMix`, which is the
 * room's.
 *
 * The TV takes its levels from room state, because the host owns the table's
 * sound. A phone must not: it may have headphones in, and a host muting the
 * TV has no business reaching them. So a phone keeps its three levels and its
 * dealer's chattiness here, and this turns them into the same
 * `SoundMix` an engine applies — the web browser's `sound.ts` and the shells'
 * `native-ui/sound.ts` both take that shape, so a seat sounds the same
 * whichever one the player is holding.
 */

export interface VolumePrefs {
  /** 0–100, the cues. */
  sound: number;
  /** 0–100, the casino floor behind them. */
  ambience: number;
  /** 0–100, the dealer. */
  voice: number;
}

/**
 * What a phone starts at. Cues full, floor bed well back: the room already
 * has a casino floor coming off the TV, so a phone only doubles it. Anyone
 * listening on headphones can bring it up. The dealer starts full too — a
 * call that cannot be made out is worse than no call.
 */
export const DEFAULT_VOLUMES: VolumePrefs = { sound: 100, ambience: 30, voice: 100 };

/** A phone's dealer starts where the room's does: the decisions. */
export const DEFAULT_CALLOUT_MODE: CalloutMode = 'essential';

const clampPercent = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.round(Math.min(100, Math.max(0, value)))
    : fallback;

/** Read three levels off anything at all; junk and gaps take the defaults. */
export function readVolumes(raw: unknown): VolumePrefs {
  if (!raw || typeof raw !== 'object') return DEFAULT_VOLUMES;
  const { sound, ambience, voice } = raw as Partial<VolumePrefs>;
  return {
    sound: clampPercent(sound, DEFAULT_VOLUMES.sound),
    ambience: clampPercent(ambience, DEFAULT_VOLUMES.ambience),
    voice: clampPercent(voice, DEFAULT_VOLUMES.voice),
  };
}

/** The same leniency for the dealer's chattiness. */
export const readCalloutMode = (raw: unknown): CalloutMode =>
  CALLOUT_MODES.includes(raw as CalloutMode) ? (raw as CalloutMode) : DEFAULT_CALLOUT_MODE;

const unit = (percent: number): number => Math.min(1, Math.max(0, percent / 100));

/**
 * This device's levels as the engine's mix, at the floor level the table is
 * at. Same arithmetic as `soundMix`, so a phone and the TV playing the same
 * cue at the same slider are at the same level — the difference is only whose
 * slider it is.
 */
export function deviceMix(prefs: VolumePrefs, on: boolean, ambience: AmbienceLevel): SoundMix {
  const master = MASTER_GAIN * unit(prefs.sound);
  return {
    enabled: on,
    cueVolume: on ? master : 0,
    ambienceVolume: on ? master * AMBIENCE_GAIN[ambience] * unit(prefs.ambience) : 0,
    ambience,
  };
}
